import React, { useState } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'translations';
import { Layout, Flex } from 'app/components/base/Layout';
import Button from 'mz-ui-kit/core/Button';
import Icon from 'app/components/base/Icon';
import { Link } from 'react-router-dom';
import { PAY_LATER_STATUS } from 'app/constants';
import { getUpdatePaymentUrl } from 'app/utils/updatePayment';
import { getGoogleWalletUrl } from 'app/utils/googleWallet';
import { getAppleWalletUrl } from 'app/utils/appleWallet';
import PayNowConfirmModal from './PayNowConfirmModal';
import builder from './builder';

import style from './style.css';
import addToGoogleWalletButton from './assets/add-to-google-wallet-condensed.svg';
import appleWalletBadge from './assets/add-to-apple-wallet.svg';

// Constants
const messages = {
  bookingNumber: { id: 'CONFIRMATION_PAGE.CONFIRMATION_NUMBER' },
  changeTrip: { id: 'CONFIRMATION_PAGE.CHANGE_TRIP' },
  changePaymentMethod: { id: 'CONFIRMATION_PAGE.CHANGE_PAYMENT_METHOD' },
  cancelTrip: { id: 'CONFIRMATION_PAGE.CANCEL_TRIP' },
  cancelled: { id: 'CONFIRMATION_PAGE.CANCELLED' },
  payNow: { id: 'CONFIRMATION_PAGE.PAY_NOW' },
};

/**
 * Component to show reservation number and reservation cancel button
 * @param {String} direction
 * @param {String} confirmationNumber
 */
export const ReservationActions = ({
  direction,
  reservationId,
  returnReservationId,
  canCancel,
  canChange,
  cancelled,
  isBefore,
  isAllowedToChange,
  payLaterStatus,
  showWallets,
  chargeSetupIntent,
}) => {
  const [payingNow, setPayingNow] = useState(false);
  const [showPayNowConfirm, setShowPayNowConfirm] = useState(false);

  if (!reservationId) return null;

  const modifiers = {
    [style[`block--${direction}`]]: true,
  };

  const showActions = !cancelled && !isBefore;

  // Buy Now, Pay Later: allow changing the payment method, or paying the
  // charge immediately, only while it's still scheduled or has failed (not
  // once paid/cancelled). Same condition for both actions.
  const canChangePayment = [
    PAY_LATER_STATUS.FAILED,
    PAY_LATER_STATUS.SCHEDULED,
  ].includes(payLaterStatus);

  // Pay Now charges the customer immediately, so a misclick shouldn't be
  // able to trigger it directly — clicking the button only opens a
  // confirmation dialog; the actual charge only fires from there.
  const handlePayNowClick = () => setShowPayNowConfirm(true);

  const handlePayNowCancel = () => setShowPayNowConfirm(false);

  const handlePayNowConfirm = () => {
    setPayingNow(true);
    const stopLoading = () => {
      setPayingNow(false);
      setShowPayNowConfirm(false);
    };
    // Both branches just clear the local loading state — the saga already
    // handles the success/error notification and, on success, the redux
    // refresh that makes this button disappear on its own.
    chargeSetupIntent({ id: reservationId }).then(stopLoading, stopLoading);
  };

  return (
    <Layout
      className={classnames(style.block, modifiers)}
      direction="column"
      nowrap
    >
      {showActions && showWallets && (
        <Layout
          className={style.walletRow}
          direction="row"
          size="noshrink"
          align="start start"
          nowrap
        >
          <a
            href={getGoogleWalletUrl(reservationId)}
            target="_blank"
            rel="noopener noreferrer"
            className={style.googleWallet}
          >
            <img src={addToGoogleWalletButton} alt="Add to Google Wallet" />
          </a>
          <a
            href={getAppleWalletUrl(reservationId)}
            target="_blank"
            rel="noopener noreferrer"
            className={style.appleWallet}
          >
            <img src={appleWalletBadge} alt="Add to Apple Wallet" />
          </a>
        </Layout>
      )}
      <Layout
        direction="row"
        size="noshrink"
        align="start start"
        wrap
      >
        {cancelled && (
          <Flex size="noshrink">
            <Button color="red" className={style.cancelled} disabled>
              <Icon small>do_not_disturb_alt</Icon>
              <FormattedMessage {...messages.cancelled} />
            </Button>
          </Flex>
        )}
        {!cancelled && canCancel && showActions && (
          <Button
            $as={Link}
            to={`/cancel-booking?id=${reservationId}&return_id=${returnReservationId}`}
            className={style.cancel}
          >
            <Icon small>do_not_disturb_alt</Icon>
            <FormattedMessage {...messages.cancelTrip} />
          </Button>
        )}
        {!cancelled && canChange && showActions && isAllowedToChange && (
          <Button
            $as={Link}
            to={`/change-booking?id=${reservationId}&return_id=${returnReservationId}`}
            className={style.change}
          >
            <FormattedMessage {...messages.changeTrip} />
          </Button>
        )}
        {showActions && canChangePayment && (
          <Button
            $as="a"
            href={getUpdatePaymentUrl(reservationId)}
            className={style.changePaymentMethod}
          >
            <FormattedMessage {...messages.changePaymentMethod} />
          </Button>
        )}
        {showActions && canChangePayment && (
          <Button
            onClick={handlePayNowClick}
            loading={payingNow}
            disabled={payingNow}
            className={style.payNow}
          >
            <FormattedMessage {...messages.payNow} />
          </Button>
        )}
      </Layout>
      {showPayNowConfirm && (
        <PayNowConfirmModal
          processing={payingNow}
          onConfirm={handlePayNowConfirm}
          onCancel={handlePayNowCancel}
        />
      )}
    </Layout>
  );
};

export default builder(ReservationActions);
